import React from 'react';
import { cn } from '@/lib/utils';
import { ThemedIcon, ControlsTheme } from '@/components/video-player/ui/ThemedIcon';

interface SwitchIconOptionProps {
  label: string;
  selected: string;
  onSelect: (value: string) => void;
  className?: string;
}

type ThemeOption = {
  id: ControlsTheme;
  name: string;
};

const THEMES: ThemeOption[] = [
  { id: 'minimal', name: 'Outline' },
  { id: 'solid', name: 'Solid' },
  { id: 'duotone', name: 'Duotone' },
  { id: 'bold', name: 'Bold' },
  { id: 'safari', name: 'Safari' },
];

// Preview de cada tema mostra um trio de ícones (Play + Volume + Maximize)
// para ficar evidente que TODA a família muda — não só o play.
const ThemePreview: React.FC<{ theme: ControlsTheme }> = ({ theme }) => {
  // Safari ('safari'): destaca os controles centrais (voltar/avançar 10s + play),
  // o traço mais marcante do tema.
  if (theme === 'safari') {
    return (
      <div className="flex items-center justify-center gap-1.5">
        <ThemedIcon name="skipBack" theme="safari" className="h-5 w-5" />
        <ThemedIcon name="play" theme="safari" className="h-5 w-5" />
        <ThemedIcon name="skipForward" theme="safari" className="h-5 w-5" />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center gap-1.5">
      <ThemedIcon name="play" theme={theme} className="h-5 w-5" />
      <ThemedIcon name="volume" theme={theme} className="h-5 w-5" />
      <ThemedIcon name="fullscreen" theme={theme} className="h-5 w-5" />
    </div>
  );
};

export const SwitchIconOption: React.FC<SwitchIconOptionProps> = ({
  label,
  selected,
  onSelect,
  className,
}) => {
  return (
    <div className={cn('space-y-2.5', className)}>
      <label className="text-[12px] font-medium text-[#374151]">{label}</label>
      <div className="grid grid-cols-5 gap-2">
        {THEMES.map((theme) => (
          <button
            key={theme.id}
            type="button"
            role="radio"
            aria-checked={selected === theme.id}
            tabIndex={0}
            title={theme.name}
            onClick={() => onSelect(theme.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(theme.id);
              }
            }}
            className={cn(
              'flex flex-col items-center justify-center px-2 py-2.5 h-[78px] rounded-xl border transition-all duration-200 group hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]',
              selected === theme.id
                ? 'border-[#1D4FFF] bg-[#F5F8FF] text-[#1D4FFF] shadow-sm'
                : 'border-[#E6E6F0] hover:border-[#1D4FFF]/40 bg-white text-[#374151]'
            )}
          >
            <div className="flex items-center justify-center mb-1.5 min-h-[24px]">
              <ThemePreview theme={theme.id} />
            </div>
            <div className="text-[10px] font-semibold text-center leading-tight">
              {theme.name}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
